/**
 * cas-6e1f: admin links keep the locale of the page they are opened from.
 *
 * i18n serves every admin page under its locale prefix too (`/es/admin/...`),
 * but the admin shell and pages wrote their link targets as raw `/admin/...`
 * strings, so from /es/admin/home any click landed on the English page. Every
 * admin link target goes through `adminLink(target, currentPath)`: under a
 * non-default locale it gains that prefix; English URLs are unchanged. A
 * static guard (utils/__tests__/admin-link-guard.test.ts) keeps raw literals out.
 *
 * Pure: no Nuxt imports, so the shell, pages, stores and utils all share it.
 */
import { localeAdminPrefix } from '~/utils/admin-redirects';
import { stripLocalePrefix } from '~/utils/public-audit-path';

/** `target` (an absolute in-app path) under the locale prefix of `fromPath`. */
export function adminLink(target: string, fromPath: string | null | undefined): string {
  const prefix = fromPath ? localeAdminPrefix(fromPath) : '';
  if (!prefix || !target.startsWith('/') || target.startsWith('//')) return target;
  if (localeAdminPrefix(target)) return target;
  return target === '/' ? prefix : `${prefix}${target}`;
}

/** Same admin page, ignoring the locale prefix and the query. */
export function isSameAdminPage(a: string, b: string): boolean {
  const path = (p: string) => stripLocalePrefix(p.split(/[?#]/)[0] ?? p);
  return path(a) === path(b);
}
